import { useInfiniteQuery } from 'react-query';

import { baseApiUrl, fetcher, ServerErrorResponse } from 'data_sources/constants';

import type { AccreditationBody } from '../../_types/awb';
import type { PaginatedApiCollectionResponse } from 'types/api_response';

export async function getAccreditationBodies<T extends AccreditationBody>(
  params = {},
  pageParam?: string,
  signal?: AbortSignal
): Promise<PaginatedApiCollectionResponse<T>> {
  const url = `${baseApiUrl()}/awb/accreditation-body/v1/accreditation-bodies`;

  const paramString = new URLSearchParams(
    Object.entries({ ...params })
      .filter(([_k, v]) => v != null)
      .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {})
  ).toString();

  const fullUrl = pageParam || `${url}?${paramString}`;

  const data: PaginatedApiCollectionResponse<T> = await fetcher({
    url: fullUrl,
    method: 'get',
    options: { signal },
  });

  return data as PaginatedApiCollectionResponse<T>;
}

export default function useAccreditingBodies<T extends AccreditationBody>(
  params = {},
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const url = `${baseApiUrl()}/awb/accreditation-body/v1/accreditation-bodies`;

  return useInfiniteQuery<PaginatedApiCollectionResponse<T>, Error>(
    ['accrediting_bodies', params],
    async ({ pageParam, signal }) => getAccreditationBodies<T>(params, pageParam, signal),
    {
      ...options,
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        if (lastPage.nextToken && lastPage.content.length > 0) {
          const paramString = new URLSearchParams(
            Object.entries({
              ...params,
              nextToken: lastPage.nextToken,
            })
              .filter(([_k, v]) => v != null)
              .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {})
          ).toString();

          return `${url}?${paramString}`;
        }
      },
    }
  );
}
